import { DateTime } from "luxon";
import FinancingHelper from "../../helpers/FinancingHelper";

export default class Assumptions {
  constructor({
    dealType = "STOCK",
    basis = "IRR",
    proceeds = "PR",
    financeRate = 0,
    reinvestRate = 0,
    independence = "AUTO",
    autoAssumptions = {},
    manualPurchasePrice = 0,
    manualTransactionCosts = 0,
    manualClosingFees = 0,
    manualTargetDebt = 0,
    manualCash = 0,
    manualMinimumCashBalance = 0,
    manualAdditionalDebt = 0,
    manualDebtFees = 0,
    manualRolloverEquity = 0,
    manualExitYear = 10,
    exitProceedsType = "AUTO",
    terminalValue = 0,
    manualExitProceeds = 0,
    exitFeesPercent = 0,
    otherExitFees = 0,
    manualOutstandingDebt = 0,
    manualFreeCashFlow = [],
    GPContributionAmount = 0,
    CMOwnershipPercent = 0,
    GPManagementFeePercent = 0,
    LPManagementFeePercent = 0,
    GPAdministrativeFeePercent = 0,
    LPAdministrativeFeePercent = 0,
    GPDirectorFeePercent = 0,
    LPDirectorFeePercent = 0,
    GPPlacementFeePercent = 0,
    LPPlacementFeePercent = 0,
    scheduleInput = [{
      tranche: false,
      hurdle: 0,
      multiHurdle: 1,
      promote: 0,
      catchUp: false,
      catchUpPercent: 0,
      catchUpGPSplit: 50,
    }],
    clawback = false,
  }) {
    this.dealType = dealType;
    this.basis = basis;
    this.proceeds = proceeds;
    this.financeRate = financeRate;
    this.reinvestRate = reinvestRate;
    this.independence = independence;
    this.autoAssumptions = autoAssumptions;
    this.manualPurchasePrice = manualPurchasePrice;
    this.manualTransactionCosts = manualTransactionCosts;
    this.manualClosingFees = manualClosingFees;
    this.manualTargetDebt = manualTargetDebt;
    this.manualCash = manualCash;
    this.manualMinimumCashBalance = manualMinimumCashBalance;
    this.manualAdditionalDebt = manualAdditionalDebt;
    this.manualDebtFees = manualDebtFees;
    this.manualRolloverEquity = manualRolloverEquity;
    this.manualExitYear = manualExitYear;
    this.exitProceedsType = exitProceedsType;
    this.terminalValue = terminalValue;
    this.manualExitProceeds = manualExitProceeds;
    this.exitFeesPercent = exitFeesPercent;
    this.otherExitFees = otherExitFees;
    this.manualOutstandingDebt = manualOutstandingDebt;
    this.manualFreeCashFlow = manualFreeCashFlow;
    this.GPContributionAmount = GPContributionAmount;
    this.CMOwnershipPercent = CMOwnershipPercent;
    this.GPManagementFeePercent = GPManagementFeePercent;
    this.LPManagementFeePercent = LPManagementFeePercent;
    this.GPAdministrativeFeePercent = GPAdministrativeFeePercent;
    this.LPAdministrativeFeePercent = LPAdministrativeFeePercent;
    this.GPDirectorFeePercent = GPDirectorFeePercent;
    this.LPDirectorFeePercent = LPDirectorFeePercent;
    this.GPPlacementFeePercent = GPPlacementFeePercent;
    this.LPPlacementFeePercent = LPPlacementFeePercent;
    this.scheduleInput = scheduleInput;
    this.clawback = clawback;
  }

  get purchasePrice() {
    return this.independence === "AUTO" ? this.autoAssumptions.purchasePrice : this.manualPurchasePrice;
  }

  get transactionCosts() {
    return this.independence === "AUTO" ? this.autoAssumptions.transactionCosts : this.manualTransactionCosts;
  }

  get closingFees() {
    return this.independence === "AUTO" ? this.autoAssumptions.closingFees : this.manualClosingFees;
  }

  get targetDebt() {
    return this.independence === "AUTO" ? this.autoAssumptions.targetDebt : this.manualTargetDebt;
  }

  get cash() {
    return this.independence === "AUTO" ? this.autoAssumptions.cash : this.manualCash;
  }

  get minimumCashBalance() {
    return this.independence === "AUTO" ? this.autoAssumptions.minimumCashBalance : this.manualMinimumCashBalance;
  }

  get additionalDebt() {
    return this.independence === "AUTO" ? this.autoAssumptions.additionalDebt : this.manualAdditionalDebt;
  }

  get debtFees() {
    return this.independence === "AUTO" ? this.autoAssumptions.debtFees : this.manualDebtFees;
  }

  get rolloverEquity() {
    return this.independence === "AUTO" ? this.autoAssumptions.rolloverEquity : this.manualRolloverEquity;
  }

  get totalEquity() {
    return this.purchasePrice + this.transactionCosts + this.targetDebt + this.debtFees
      - this.cash + this.minimumCashBalance - this.additionalDebt - this.rolloverEquity;
  }

  get exitYear() {
    return this.independence === "AUTO" ? this.autoAssumptions.exitYear : this.manualExitYear;
  }

  get exitProceeds() {
    return this.exitProceedsType === "AUTO" ? this.terminalValue : this.manualExitProceeds;
  }

  get exitFees() {
    return this.exitProceeds * this.exitFeesPercent / 100;
  }

  get grossProceeds() {
    return this.exitProceeds - this.exitFees - this.otherExitFees;
  }

  get outstandingDebt() {
    return this.independence === "AUTO" ? this.autoAssumptions.outstandingDebt : this.manualOutstandingDebt;
  }

  get netProceeds() {
    return this.grossProceeds - this.outstandingDebt;
  }

  get yearsFormatted() {
    let manualYears = [this.autoAssumptions.yearsFormatted[0]];
    for(let i = 1; i < this.exitYear; i++)
      manualYears[i] = DateTime.fromJSDate(new Date(manualYears[i - 1])).plus({ years: 1 }).toFormat("LLLL y");
    return this.independence === "AUTO" ? this.autoAssumptions.yearsFormatted : manualYears;
  }

  get freeCashFlow() {
    if(this.independence === "AUTO") return this.autoAssumptions.freeCashFlow;
    
    while(this.manualFreeCashFlow.length < this.exitYear) this.manualFreeCashFlow.push({
      date: this.yearsFormatted[this.manualFreeCashFlow.length],
      value: 0,
      disabled: false
    });
    while(this.manualFreeCashFlow.length > this.exitYear) this.manualFreeCashFlow.pop();
    return this.manualFreeCashFlow;
  }

  get LPContributionAmount() {
    return this.totalEquity - this.GPContributionAmount;
  }

  get GPContributionPercent() {
    return this.GPContributionAmount / this.totalEquity * 100;
  }

  get LPContributionPercent() {
    return this.LPContributionAmount / this.totalEquity * 100;
  }

  get GPOwnershipPercent() {
    return (100 - this.CMOwnershipPercent) * this.GPContributionPercent / 100;
  }

  get LPOwnershipPercent() {
    return (100 - this.CMOwnershipPercent) * this.LPContributionPercent / 100;
  }

  get GPClosingFeeAmount() {
    return this.closingFees * this.GPContributionPercent / 100;
  }

  get LPClosingFeeAmount() {
    return this.closingFees * this.LPContributionPercent / 100;
  }

  get GPManagementFeeAmount() {
    return this.totalEquity * this.GPManagementFeePercent / 100;
  }

  get LPManagementFeeAmount() {
    return this.totalEquity * this.LPManagementFeePercent / 100;
  }

  get GPAdministrativeFeeAmount() {
    return this.totalEquity * this.GPAdministrativeFeePercent / 100;
  }

  get LPAdministrativeFeeAmount() {
    return this.totalEquity * this.LPAdministrativeFeePercent / 100;
  }

  get GPDirectorFeeAmount() {
    return this.totalEquity * this.GPDirectorFeePercent / 100;
  }

  get LPDirectorFeeAmount() {
    return this.totalEquity * this.LPDirectorFeePercent / 100;
  }

  get GPPlacementFeeAmount() {
    return this.totalEquity * this.GPPlacementFeePercent / 100;
  }

  get LPPlacementFeeAmount() {
    return this.totalEquity * this.LPPlacementFeePercent / 100;
  }

  get GPTotalAnnualFees() {
    return this.GPManagementFeeAmount + this.GPAdministrativeFeeAmount + this.GPDirectorFeeAmount + this.GPPlacementFeeAmount;
  }

  get LPTotalAnnualFees() {
    return this.LPManagementFeeAmount + this.LPAdministrativeFeeAmount + this.LPDirectorFeeAmount + this.LPPlacementFeeAmount;
  }

  get schedule() {
    const { scheduleInput, GPContributionPercent, LPContributionPercent } = this;
    return scheduleInput.map((obj) => ({
      ...obj,
      trancheGPSplit: (100 - obj.promote) * GPContributionPercent / 100 + obj.promote,
      trancheLPSplit: (100 - obj.promote) * LPContributionPercent / 100,
      catchUpLPSplit: 100 - obj.catchUpGPSplit,
    }))
  }

  get netCashFlow() {
    const { freeCashFlow, GPTotalAnnualFees, LPTotalAnnualFees, netProceeds, outstandingDebt } = this;
// let temptestCashFlow = [{value: 1300000},{value: 45000},{value: 5000},{value: 30000},{value: 200000},{value: -1000000},
//       {value: 300000},{value: 200000},{value: 200000},{value: -9650000}];
    return this.getArraySum(
      this.getArraySum(freeCashFlow, GPTotalAnnualFees + LPTotalAnnualFees, { minus: true, singleVariable: true }),
      netProceeds - outstandingDebt,
      { singleVariable: true, lastYearOnly: true }
    );
  }

  get contributionsEngine() {
    //equity holders only contribute when cash flow is less than zero
    //ref: contributions have minus values, as they are outcome for equity holders
    const { netCashFlow, LPContributionPercent, GPContributionPercent } = this;
    return {
      LP: netCashFlow.map((obj) => ({...obj, value: obj.value > 0 ? 0 : obj.value * LPContributionPercent / 100})),
      GP: netCashFlow.map((obj) => ({...obj, value: obj.value > 0 ? 0 : obj.value * GPContributionPercent / 100})),
      remainCashFlow: netCashFlow.map((obj) => ({...obj, value: obj.value > 0 ? obj.value : 0})),
    };
  }

  get proRataEngine() {
    //if we do pro-rata, we distribute cash flow according to the ownership percentage
    const { proceeds, LPOwnershipPercent, GPOwnershipPercent, CMOwnershipPercent } = this;
    const cashFlow = this.contributionsEngine.remainCashFlow;

    return {
      LP: cashFlow.map((obj) => ({...obj, value: proceeds === "PR" ? obj.value * LPOwnershipPercent / 100 : 0})),
      GP: cashFlow.map((obj) => ({...obj, value: proceeds === "PR" ? obj.value * GPOwnershipPercent / 100 : 0})),
      CM: cashFlow.map((obj) => ({...obj, value: proceeds === "PR" ? obj.value * CMOwnershipPercent / 100 : 0})),
      remainCashFlow: cashFlow.map((obj) => ({...obj, value: proceeds === "PR" ? 0 : obj.value})),
    };
  }

  get scheduleEngine() {
    const {
      yearsFormatted,
      basis,
      grossProceeds,
      schedule,
      LPContributionAmount,
      LPContributionPercent,
      GPContributionPercent,
      LPOwnershipPercent,
      GPOwnershipPercent,
      CMOwnershipPercent,
      contributionsEngine,
      clawback
    } = this;
    let remainCashFlow = this.proRataEngine.remainCashFlow.map((obj) => ({...obj}));
    let totalLPDistArray = yearsFormatted.map((year) => ({ date: year, value: 0 }));
    let totalGPDistArray = yearsFormatted.map((year) => ({ date: year, value: 0 }));
    let totalCMDistArray = yearsFormatted.map((year) => ({ date: year, value: 0 }));
    const len = remainCashFlow.length;

    for(let i = 0; i < len; i++) {
      //schedule distribution
      schedule.map((sche) => {
        //tranche starting balance
        if(i === 0) {
          sche.balance = LPContributionAmount;      //first year's starting balance is the initial LP contribution
          sche.multiBal = LPContributionAmount * sche.multiHurdle;    //Equity Multiple balance
        }
        else {
          sche.balance = sche.balance - contributionsEngine.LP[i - 1].value - totalLPDistArray[i - 1].value;
          sche.multiBal = sche.multiBal - contributionsEngine.LP[i - 1].value * sche.multiHurdle - totalLPDistArray[i - 1].value;
        }

        //balance + interest(for IRR/Multiple) / interest(for COC)
        if(basis === "COC") sche.balance = 
          (LPContributionAmount - contributionsEngine.LP.reduce((acc, cont, index) => acc + (index <= i ? cont.value : 0), 0)) * sche.hurdle / 100;
            //COC multiple part of cumulated contributions, contriAmount has positive values, contriEngine has negative values
        else sche.balance = sche.balance * (1 + sche.hurdle / 100);    //interest plus

        //initialize the variable for sum of total LP, GP distribution for that schedule;
        if(i === 0) {
          sche.LPDistSum = 0;
          sche.GPDistSum = 0;
        }

        //tranche distribution
        let LPDist = sche.tranche
          ? Math.max(
              Math.min(
                (basis === "MULTI" ? Math.max(sche.balance, sche.multiBal) : sche.balance) - totalLPDistArray[i].value,
                (remainCashFlow[i].value - (basis === "COC" && i === len - 1 ? grossProceeds : 0)) * sche.trancheLPSplit / 100
              ),
              0
            )
          : 0;
          //tranche LP dist. if tranche is false, dist = 0. dist can't less than 0. dist can't bigger than cash split
          //On Cash on Cash Model, we separate sale proceeds from the cash flow
          //the purpose of tranche is to cover the remaining balance with total dist, so dist + prior dist <= balance
        let GPDist = sche.tranche ? LPDist / sche.trancheLPSplit * sche.trancheGPSplit : 0;
        sche.LPDistSum += LPDist;                       //increase that schedule's total LP distribution
        sche.GPDistSum += GPDist;                       //that schedule's total GP distribution
        totalLPDistArray[i].value += LPDist;            //increase that year's item of LP distribution array
        totalGPDistArray[i].value += GPDist;            //item of GP distribution array
        remainCashFlow[i].value -= LPDist + GPDist;     //separate tranche's distribution from cash flow

        //catch-up distribution
        let cumGPDist = basis === "COC"
          ? totalGPDistArray[i].value
          : totalGPDistArray.reduce((acc, dist, index) => acc + (index <= i ? dist.value : 0), 0);
        let cumDist = basis === "COC"
          ? totalLPDistArray[i].value + totalGPDistArray[i].value
          : cumGPDist + totalLPDistArray.reduce((acc, dist, index) => acc + (index <= i ? dist.value : 0), 0);
        GPDist = sche.catchUp ?
          Math.max(
            Math.min(
              sche.catchUpGPSplit * (cumDist * sche.catchUpPercent / 100 - cumGPDist) / (sche.catchUpGPSplit - sche.catchUpPercent),
              (remainCashFlow[i].value - (basis === "COC" && i === len - 1 ? grossProceeds : 0)) * sche.catchUpGPSplit / 100
            ), 0)
          : 0;
          //catch-up LP dist. if tranche is false, dist = 0. dist can't less than 0. dist can't bigger than catch-up amount or cash split
          //line 236 explanation: catchUpPercent * (cumDist + catchupGPDist + catchupLPDist) = cumGPDist + catchupGPDist
        LPDist = sche.catchUp ? GPDist / sche.catchUpGPSplit * sche.catchUpLPSplit : 0;//must calculate GPDist fist as catchUpLPSplit can be 0
        sche.LPDistSum += LPDist;                       //increase that schedule's total LP distribution
        sche.GPDistSum += GPDist;                       //that schedule's total GP distribution
        totalLPDistArray[i].value += LPDist;            //increase that year's item of LP distribution array
        totalGPDistArray[i].value += GPDist;            //item of GP distribution array
        remainCashFlow[i].value -= LPDist + GPDist;     //separate tranche's distribution from cash flow
        
        //ultimate ending balance(for clawback obligation)
        sche.endingBal = sche.balance - contributionsEngine.LP[i].value - totalLPDistArray[i].value;
      })

      //sale proceeds distribution(for Cash on Cash Model)
      if(basis === "COC" && i === len - 1) {
        totalLPDistArray[i].value += grossProceeds * LPContributionPercent / 100;
        totalGPDistArray[i].value += grossProceeds * GPContributionPercent / 100;
        remainCashFlow[i].value -= grossProceeds * LPContributionPercent / 100 + grossProceeds * GPContributionPercent / 100;
      }

      //residual distribution
      totalLPDistArray[i].value += remainCashFlow[i].value * LPOwnershipPercent / 100;
      totalGPDistArray[i].value += remainCashFlow[i].value * GPOwnershipPercent / 100;
      totalCMDistArray[i].value += remainCashFlow[i].value * CMOwnershipPercent / 100;
    }

    //clawback engine
    let eligibleLPDist = totalLPDistArray.reduce((acc, dist) => acc + dist.value, 0);
    let eligibleGPDist = totalGPDistArray.reduce((acc, dist) => acc + dist.value, 0);
    let promoteBeforeClawback = eligibleGPDist - (eligibleLPDist + eligibleGPDist) * GPContributionPercent / 100
    let clawbackAmount = schedule.reduce((acc, sche) => {
      let totalObli = basis === "COC" 
        ? len * (LPContributionAmount - contributionsEngine.LP.reduce((acc, cont) => acc + cont.value, 0)) * sche.hurdle / 100
          + grossProceeds * LPContributionPercent / 100 - totalLPDistArray.reduce((acc, dist) => acc + dist.value, 0)
        : sche.endingBal;
      sche.clawback = sche.tranche && clawback ?
        Math.max(Math.min(totalObli - acc, eligibleGPDist - (eligibleLPDist + eligibleGPDist) * sche.trancheGPSplit / 100), 0) : 0;
          //clawback obligation = totalObli - acc(cumulated clawback). Because we add clawback amount to LP's total distribution
          //GP is only responsible for promotion. eligible Total Dist * sche.trancheGPSplit / 100 is what GP must be paid at least
      eligibleLPDist -= sche.LPDistSum;
      eligibleGPDist -= sche.GPDistSum + sche.clawback / (sche.trancheLPSplit / 100);
      return acc + sche.clawback;
    }, 0);

    return {
      LP: this.getArraySum(totalLPDistArray, clawbackAmount, { singleVariable: true, lastYearOnly: true }),
      GP: this.getArraySum(totalGPDistArray, clawbackAmount, { minus: true, singleVariable: true, lastYearOnly: true }),
      CM: totalCMDistArray,
      clawbackAmount,
      GPPromote: promoteBeforeClawback - clawbackAmount
    };
  }

  get totalCashFlow() {
    const {
      yearsFormatted,
      LPContributionAmount,
      GPContributionAmount,
      financeRate,
      reinvestRate,
      contributionsEngine,
      proRataEngine,
      scheduleEngine
    } = this;
    let yearsArray = [...yearsFormatted];
    let cashFlow = {};
    let totalProceeds = {};
    let IRR = {};
    let EM = {};
    let MIRR;

    let LPArray = this.getArraySum(
      contributionsEngine.LP,
      this.getArraySum(proRataEngine.LP, scheduleEngine.LP)     //pro-rata and schedule results have valid values according to the basis
    );
    let GPArray = this.getArraySum(
      contributionsEngine.GP,
      this.getArraySum(proRataEngine.GP, scheduleEngine.GP)
    );
    let CMArray = this.getArraySum(proRataEngine.CM, scheduleEngine.CM);

    //add base year
    yearsArray.splice(0, 0, DateTime.fromJSDate(new Date(yearsFormatted[0])).plus({ years: -1 }).toFormat("LLLL y"));
    LPArray.splice(0, 0, {date: yearsArray[0], value: -LPContributionAmount});
    GPArray.splice(0, 0, {date: yearsArray[0], value: -GPContributionAmount});
    CMArray.splice(0, 0, {date: yearsArray[0], value: 0});

    //cashFlow
    cashFlow = {
      LP: LPArray,
      GP: GPArray,
      CM: CMArray,
      total: yearsArray.map((year, index) => ({ date: year, value: LPArray[index].value + GPArray[index].value + CMArray[index].value })),
    };

    Object.keys(cashFlow).forEach((key) => {
      totalProceeds[key] = cashFlow[key].reduce((acc, obj, index) => (acc + (index > 0 ? obj.value : 0)), 0);     //doesn't include base year
      if(key !== "CM") {
        IRR[key] = cashFlow[key][0].value >= 0 ? NaN : FinancingHelper.XIRR(cashFlow[key]) * 100;
        EM[key] = FinancingHelper.EM(cashFlow[key]);
      }
    });
    MIRR = FinancingHelper.MIRR(cashFlow.LP, financeRate / 100, reinvestRate / 100) * 100;

    return { yearsArray, cashFlow, totalProceeds, IRR, EM, MIRR };
  }

  getArraySum(array1, array2, options = { minus: false, singleVariable: false, lastYearOnly: false }) {
    //add element values of arrays
    //minus: true => calculate subtraction
    //singleVariable: true => subtract a number from every array elements
    //lastYearOnly: true => subtract a number from the last element of the array

    const { yearsFormatted } = this;
    const len = yearsFormatted.length;

    return yearsFormatted.map(
      (year, index) =>
        ({
          date: year,
          value: options.minus ?
            options.singleVariable ? 
              (options.lastYearOnly && index !== len - 1) ? Number(array1[index].value) : Number(array1[index].value) - Number(array2)
              :
              Number(array1[index].value) - Number(array2[index].value)
            :
            options.singleVariable ?
              (options.lastYearOnly && index !== len - 1) ? Number(array1[index].value) : Number(array1[index].value) + Number(array2)
              :
              Number(array1[index].value) + Number(array2[index].value),
        })
    );
  }

  getArrayMultiple(array, variable) {
    return array.map((obj) => ({...obj, value: obj.value * variable}));
  }
}
